import {RegisterOptions} from 'react-hook-form';
import {t} from '@lingui/core/macro';

const MAX_DOMAIN_LENGTH = 253;
const MAX_LABEL_LENGTH = 63;
const IPV4_PATTERN = /^\d{1,3}(\.\d{1,3}){3}$/;
const ALLOWED_CHARS_PATTERN = /^[\p{L}\p{N}.-]+$/u;

export function extractDomain(value: string): string {
    const withoutProtocol = value.trim().toLowerCase().replace(/^[a-z][a-z\d+.-]*:\/\//, '');
    const host = (withoutProtocol.split(/[/?#]/)[0].split('@').pop() ?? '')
        .replace(/:\d+$/, '')
        .replace(/\.$/, '');

    return host.replace(/^www\./, '');
}

export function validateDomain(value: string): string | true {
    const domain = extractDomain(value ?? '');

    if (domain === '') {
        return t`Укажите домен`;
    }

    if (domain.includes(':') || domain.includes('[')) {
        return t`Укажите домен, а не IP-адрес`;
    }

    if (IPV4_PATTERN.test(domain)) {
        return t`Укажите домен, а не IP-адрес`;
    }

    if (domain.length > MAX_DOMAIN_LENGTH) {
        return t`Домен слишком длинный`;
    }

    if (!ALLOWED_CHARS_PATTERN.test(domain)) {
        return t`Домен содержит недопустимые символы`;
    }

    const labels = domain.split('.');

    if (labels.length < 2) {
        return t`Некорректный домен`;
    }

    const hasBrokenLabel = labels.some(label =>
        label === '' || label.length > MAX_LABEL_LENGTH || label.startsWith('-') || label.endsWith('-'),
    );

    if (hasBrokenLabel) {
        return t`Некорректный домен`;
    }

    const tld = labels[labels.length - 1];

    if (tld.length < 2 || /\d/.test(tld)) {
        return t`Некорректный домен`;
    }

    return true;
}

export function validateMultipleDomains(value: string): string | true {
    if (!value || value.trim() === '') {
        return t`Укажите домен`;
    }

    const domains = value.split(/\r?\n/).filter(domain => domain.trim() !== '');

    if (domains.length === 0) {
        return t`Укажите домен`;
    }

    const errors = domains
        .map(domain => ({domain, result: validateDomain(domain)}))
        .filter(({result}) => result !== true)
        .map(({domain, result}) => `• ${result}: ${domain.trim()}`);

    return errors.length === 0 ? true : errors.join('\n');
}

export function createSingleDomainValidator(): RegisterOptions {
    return {
        required: t`Укажите домен`,
        setValueAs: (value: string) => (value ? value.trim() : ''),
        validate: (value: string) => validateDomain(value),
    };
}
